/**
 * v1.2 Batch 3-B/C/D/E/F：教务模块全部页面
 *
 * 单文件聚合 5 个页面 + 共享组件：
 *   - StudentsListPage     学员对接列表（/academic/students）
 *   - StudentDetailPage    学员详情（/academic/students/:id）
 *   - OverviewPage         教务总表（/academic/overview）
 *   - ReportsPage          教务日报（/academic/reports）
 *   - DashboardPage        教务仪表盘（/academic/dashboard）
 *
 * 严格走 var(--*) token + ui.tsx 组件。
 */
import { useEffect, useMemo, useState, useRef } from 'react';
import { Input, Modal, Select, Table, Tag, Drawer, message, Tabs } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { $ } from '../../lib/api';
import { PageHeader, Pill, Btn, HomeCard, TableWrap, StatCard, EmptyHint } from '../../components/ui';
import { useAuthStore } from '../../store/auth';
// ux9 日期统一出口（fmtRelative 已内置空值兜底）
import { fmtRelative } from '../../lib/format';
// v1.2-rc2 fix ②：拆到独立文件避免 jsxDEV function hoisting 失效白屏
import { GuidanceInsightCard } from '../../components/academic/GuidanceInsightCard';
// Wave-Viz 5：教务交付进度看板（各班型完成度分布 + 逾期任务数）
import { AcademicDeliveryBoard } from '../../components/viz/AcademicDeliveryBoard';

const STAGE_LABELS: Record<string, string> = {
  select_platform: '选平台',
  register_shop: '注册店铺',
  select_product: '选品',
  list_product: '产品上架',
  optimize: '产品优化',
  analyze_data: '产品数据查看分析',
  ad_placement: '广告投放',
  first_order: '出单',
  no_followup: '未激活',
};
const STAGES = Object.keys(STAGE_LABELS);
const LEVEL_TONE: Record<string, 'success' | 'primary' | 'warning' | 'danger' | 'default'> = {
  A: 'success', B: 'primary', C: 'warning',
};
const URGENCY_TONE: Record<string, 'default' | 'warning' | 'danger'> = {
  normal: 'default', important: 'warning', urgent: 'danger',
};
const URGENCY_LABEL: Record<string, string> = {
  normal: '一般', important: '重要', urgent: '紧急',
};

/* ──────────────────────────────────────────────────────────────────
 * B：学员对接列表
 * ────────────────────────────────────────────────────────────────── */
export function StudentsListPage() {
  const nav = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [stage, setStage] = useState<string | undefined>();
  const [level, setLevel] = useState<string | undefined>();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<any>({
    queryKey: ['academic-students', { keyword, stage, level, page }],
    queryFn: () => $.get('/academic/students', {
      keyword: keyword || undefined, stage, level, page, pageSize: 30,
    }),
  });

  return (
    <>
      <PageHeader
        breadcrumb={['教务流程', '学员对接']}
        title="学员对接"
        subtitle="本教务负责的学员列表（主管 / 总监见全部）"
      />
      <div className="work-content">
        <HomeCard title={`学员（${data?.total ?? 0}）`} action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Input placeholder="姓名 / 手机 / 微信"
                   value={keyword} onChange={(e) => setKeyword(e.target.value)}
                   style={{ width: 200 }} allowClear />
            <Select placeholder="阶段" allowClear style={{ width: 140 }}
                    value={stage} onChange={setStage}
                    options={STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] }))} />
            <Select placeholder="等级" allowClear style={{ width: 100 }}
                    value={level} onChange={setLevel}
                    options={['A', 'B', 'C'].map((v) => ({ value: v, label: v }))} />
          </div>
        }>
          <TableWrap>
            <Table
              rowKey="id"
              loading={isLoading}
              dataSource={data?.items ?? []}
              pagination={{
                current: page, pageSize: 30, total: data?.total ?? 0,
                onChange: setPage, showSizeChanger: false,
              }}
              size="middle"
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: <EmptyHint arrow={false}>暂无数据</EmptyHint> }}
              columns={[
                { title: '学员', dataIndex: ['customer', 'nickname'], width: 160,
                  render: (_v: any, r: any) => (
                    <Link to={`/academic/students/${r.id}`}
                          style={{ color: 'var(--primary, #3370FF)', fontWeight: 500 }}>
                      {r.customer?.realName ?? r.customer?.nickname ?? '—'}
                    </Link>
                  ),
                },
                { title: '当前阶段', dataIndex: 'currentStage', width: 140,
                  render: (s: string) => <Pill tone="default">{STAGE_LABELS[s] ?? s}</Pill>,
                },
                { title: '等级', dataIndex: 'studentLevel', width: 80,
                  render: (l: string) => <Pill tone={LEVEL_TONE[l]}>{l}</Pill>,
                },
                { title: '上次联系', dataIndex: 'lastContactAt', width: 140,
                  render: (d: string) => fmtRelative(d),
                },
                { title: '服务期截止', dataIndex: 'serviceEndAt', width: 120,
                  render: (d: string) => d ?? '—',
                },
                { title: '操作', width: 100,
                  render: (_v: any, r: any) => (
                    <Btn kind="text" onClick={() => nav(`/academic/students/${r.id}`)}>查看</Btn>
                  ),
                },
              ]}
            />
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * C：学员详情
 * ────────────────────────────────────────────────────────────────── */
export function StudentDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const recordId = Number(id);

  const { data, isLoading } = useQuery<any>({
    queryKey: ['academic-student', recordId],
    queryFn: () => $.get(`/academic/students/${recordId}`),
    enabled: recordId > 0,
  });

  const [stage, setStage] = useState<string>('');
  const [todayTask, setTodayTask] = useState('');
  const [yesterdayDone, setYesterdayDone] = useState<boolean>(false);
  const [todayDone, setTodayDone] = useState<boolean>(false); // batch-fix#H35：今日任务完成开关
  const [studentQuestion, setStudentQuestion] = useState('');
  const [urgency, setUrgency] = useState<'normal' | 'important' | 'urgent'>('normal');

  // 数据加载后回填表单
  useMemo(() => {
    if (!data) return;
    setStage(data.record?.currentStage ?? 'no_followup');
    setTodayTask(data.todayTask?.todayTask ?? '');
    setYesterdayDone(!!data.todayTask?.yesterdayDone);
    setTodayDone(!!data.todayTask?.todayDone);
    setStudentQuestion(data.todayTask?.studentQuestion ?? '');
    setUrgency(data.todayTask?.questionUrgency ?? 'normal');
  }, [data]);

  const save = useMutation({
    mutationFn: () => $.patch(`/academic/students/${recordId}`, {
      currentStage: stage,
      todayTask, yesterdayDone, todayDone,
      studentQuestion, questionUrgency: urgency,
    }),
    onSuccess: () => {
      message.success('已保存');
      qc.invalidateQueries({ queryKey: ['academic-student', recordId] });
    },
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  if (isLoading || !data) return null;
  const { record, customer, followUps, assignedTo, owner } = data;

  return (
    <>
      <PageHeader
        breadcrumb={['教务流程', '学员对接', customer?.nickname ?? '—']}
        title={customer?.realName ?? customer?.nickname ?? '—'}
        tag={STAGE_LABELS[record.currentStage] ?? record.currentStage}
        subtitle={`等级 ${record.studentLevel} · 服务期至 ${record.serviceEndAt ?? '—'} · 教务 ${assignedTo?.realName ?? '未分配'}`}
      />
      <div className="work-content">
        {/* 学员基础信息 */}
        <HomeCard title="📋 学员基础信息（来自销售 CRM）">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, fontSize: 14 }}>
            <Field label="姓名" value={customer?.realName ?? customer?.nickname} />
            <Field label="手机" value={customer?.phone} />
            {/* v1.2 教务增强 D：微信优先显示真昵称，没有就「—」（不拿手机号顶） */}
            <Field label="微信" value={
              customer?.wechatNickname ||
              (customer?.wechatId && customer.wechatId !== customer.phone ? customer.wechatId : null)
            } />
            <Field label="报名版型" value={record.enrollmentVersion ?? customer?.demandType} />
            <Field label="订单金额" value={customer?.totalPaid ? `¥${Number(customer.totalPaid).toLocaleString()}` : '—'} />
            {/* v1.2 教务增强 D：销售员显示姓名而非 ID */}
            <Field label="销售员" value={owner?.realName ?? '—'} />
          </div>
        </HomeCard>

        {/* 销售前期沟通时序 */}
        <HomeCard title={`💬 销售前期沟通时序（共 ${followUps?.length ?? 0} 条）`}>
          {(followUps ?? []).length === 0 ? (
            <div style={{ padding: 16, color: 'var(--text-3, #8F959E)', fontSize: 13 }}>暂无跟进记录</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
              {(followUps ?? []).map((f: any) => (
                <div key={f.id} style={{
                  padding: 12, background: 'var(--bg-2, #F4F5F7)', borderRadius: 8, fontSize: 13,
                }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <Pill tone="default">{f.stage}</Pill>
                    <span style={{ color: 'var(--text-3, #8F959E)', fontSize: 12 }}>
                      {dayjs(f.createdAt).format('YYYY-MM-DD HH:mm')}
                    </span>
                    {f.aiMood && (
                      <Pill tone={f.aiMood === 'resistant' ? 'danger' : f.aiMood === 'positive' ? 'success' : 'default'}>
                        {f.aiMood}
                      </Pill>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-1, #1F2329)', lineHeight: 1.6 }}>
                    {(f.aiSummary ?? f.content ?? '').slice(0, 200)}{(f.aiSummary ?? f.content ?? '').length > 200 ? '…' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </HomeCard>

        {/* v1.2 教务增强 C-2 + C-3 视角①：AI 学员指导进度解析（核心模块） */}
        <GuidanceInsightCard recordId={recordId} weworkBindStatus={record.weworkBindStatus} />

        {/* v1.2 Batch 4-B：AI 学员 9 阶段总结（保留作辅助参考） */}
        <AcademicAiSummaryCard recordId={recordId} judgedStageMismatch={record.currentStage} />


        {/* 教务对接区 */}
        <HomeCard title="✏️ 教务对接区">
          <div style={{ display: 'grid', gap: 12 }}>
            <FormRow label="当前阶段">
              <Select value={stage} onChange={setStage} style={{ width: 240 }}
                      options={STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] }))} />
            </FormRow>
            <FormRow label="昨日任务完成">
              <Select value={yesterdayDone ? 'true' : 'false'}
                      onChange={(v) => setYesterdayDone(v === 'true')}
                      style={{ width: 140 }}
                      options={[{ value: 'true', label: '已完成' }, { value: 'false', label: '未完成' }]} />
            </FormRow>
            <FormRow label="今日任务完成">
              <Select value={todayDone ? 'true' : 'false'}
                      onChange={(v) => setTodayDone(v === 'true')}
                      style={{ width: 140 }}
                      options={[{ value: 'true', label: '已完成' }, { value: 'false', label: '未完成' }]} />
            </FormRow>
            <FormRow label="今日任务">
              <Input.TextArea value={todayTask}
                              onChange={(e) => setTodayTask(e.target.value)}
                              rows={3} placeholder="例：跟进 XX 平台店铺数据 / 帮学员选品" />
            </FormRow>
            <FormRow label="学员问题">
              <Input.TextArea value={studentQuestion}
                              onChange={(e) => setStudentQuestion(e.target.value)}
                              rows={2} placeholder="学员遇到的问题（如有）" />
            </FormRow>
            <FormRow label="问题紧急度">
              <Select value={urgency} onChange={setUrgency} style={{ width: 140 }}
                      options={Object.entries(URGENCY_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
            </FormRow>
            <div>
              <Btn kind="primary" disabled={save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? '保存中…' : '保存'}
              </Btn>
            </div>
          </div>
        </HomeCard>
      </div>
    </>
  );
}

/* v1.2 Batch 4-B：AI 学员总结卡（GuidanceInsightCard 已拆到独立文件 — 见 import） */
function AcademicAiSummaryCard({ recordId, judgedStageMismatch }: { recordId: number; judgedStageMismatch?: string }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const canRegen = me && ['academic_director', 'admin'].includes(me.role);

  const { data, isLoading } = useQuery<any>({
    queryKey: ['academic-ai-summary', recordId],
    queryFn: () => $.get(`/academic/students/${recordId}/ai-summary`),
    enabled: recordId > 0,
  });

  const regenerate = useMutation({
    mutationFn: () => $.post(`/academic/students/${recordId}/ai-summary`, { force: true }),
    onSuccess: () => {
      message.success('已重新生成');
      qc.invalidateQueries({ queryKey: ['academic-ai-summary', recordId] });
    },
    onError: (e: any) => message.error(e?.message ?? '生成失败'),
  });

  const json = data?.summaryJson;
  const dataSources = data?.dataSources;
  const judgedMismatch = json?.judged_stage && judgedStageMismatch && json.judged_stage !== judgedStageMismatch;

  return (
    <HomeCard title="🤖 AI 学员总结" action={
      canRegen && (
        <Btn kind="default" disabled={regenerate.isPending} onClick={() => regenerate.mutate()}>
          {regenerate.isPending ? '生成中…' : '重新生成'}
        </Btn>
      )
    }>
      {isLoading && (
        <div style={{ padding: 16, color: 'var(--text-3, #8F959E)', fontSize: 13, textAlign: 'center' }}>
          AI 分析中...
        </div>
      )}
      {!isLoading && !data && (
        <div style={{ padding: 16, color: 'var(--text-3, #8F959E)', fontSize: 13, textAlign: 'center' }}>
          尚未生成总结{canRegen ? '。点击右上「重新生成」即可。' : '，等待 cron 每日 03:00 预生成 A/B 类学员。'}
        </div>
      )}
      {!isLoading && data && (
        <div style={{ display: 'grid', gap: 12 }}>
          {/* 一句话 + 阶段判断 */}
          <div style={{
            padding: 12, background: 'var(--primary-bg, #EAF2FF)', borderRadius: 8,
            fontSize: 14, color: 'var(--text-1, #1F2329)', lineHeight: 1.6,
          }}>
            {json?.summary ?? '（无总结）'}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <span style={{ color: 'var(--text-3, #8F959E)' }}>AI 判定阶段：</span>
            <Pill tone={judgedMismatch ? 'danger' : 'success'}>
              {STAGE_LABELS[json?.judged_stage] ?? json?.judged_stage}
            </Pill>
            <span style={{ color: 'var(--text-3, #8F959E)' }}>
              置信度 {((json?.stage_confidence ?? 0) * 100).toFixed(0)}%
            </span>
            {judgedMismatch && (
              <span style={{ color: 'var(--danger, #FF4D4F)', fontWeight: 500 }}>
                ⚠ 与教务录入的「{STAGE_LABELS[judgedStageMismatch!] ?? judgedStageMismatch}」不一致
              </span>
            )}
          </div>
          {/* 需求 / 痛点 / 下一步 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <AiListBox title="需求" items={json?.needs ?? []} tone="primary" />
            <AiListBox title="痛点" items={json?.pain_points ?? []} tone="warning" />
            <AiListBox title="下一步" items={json?.next_actions ?? []} tone="success" />
          </div>
          {/* 数据源 */}
          <div style={{ fontSize: 11, color: 'var(--text-3, #8F959E)', textAlign: 'right' }}>
            数据源：企微 {dataSources?.wework ?? 0} 条 · 跟进 {dataSources?.follow_ups ?? 0} 条 · 教务任务 {dataSources?.academic_tasks ?? 0} 条 ·
            模型 {data?.model ?? '—'} · 生成于 {fmtRelative(data?.generatedAt)}
          </div>
        </div>
      )}
    </HomeCard>
  );
}

function AiListBox({ title, items, tone }: { title: string; items: string[]; tone: 'primary' | 'warning' | 'success' }) {
  return (
    <div style={{
      padding: 10, background: 'var(--bg-2, #F4F5F7)', borderRadius: 8,
      minHeight: 80,
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)', marginBottom: 6 }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)' }}>—</div>
      ) : (
        <ul style={{ paddingLeft: 16, margin: 0, fontSize: 13, color: 'var(--text-1, #1F2329)', lineHeight: 1.6 }}>
          {items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      )}
    </div>
  );
}

/** v1.2 教务增强 C-3 视角②：总表内嵌指导进度小 cell */
function OverviewInsightCell({ recordId }: { recordId: number }) {
  // batch-2 N+1 缓解：IntersectionObserver 懒加载，仅滚动到可视的行才查 guidance-insight
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); io.disconnect(); }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);
  const { data } = useQuery<any>({
    queryKey: ['academic-guidance-insight', recordId],
    queryFn: () => $.get(`/academic/students/${recordId}/guidance-insight`),
    enabled: visible && recordId > 0,
    staleTime: 5 * 60_000,
  });
  if (!data) return <span ref={ref} style={{ color: 'var(--text-3, #8F959E)', fontSize: 12 }}>—</span>;
  const ij = data.insightJson ?? {};
  const stage = ij.current_guidance_stage;
  const date = ij.last_communication?.date;
  return (
    <div style={{ fontSize: 12 }}>
      <Pill tone={stage && stage !== 'no_followup' ? 'primary' : 'default'}>
        {STAGE_LABELS[stage] ?? stage ?? '未生成'}
      </Pill>
      {date && date !== '暂无足够信息' && (
        <div style={{ color: 'var(--text-3, #8F959E)', marginTop: 2 }}>上次 {date}</div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: any }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--text-1, #1F2329)' }}>{value || '—'}</div>
    </div>
  );
}

function FormRow({ label, children }: { label: string; children: any }) {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ width: 100, fontSize: 13, color: 'var(--text-2, #4E5969)', paddingTop: 6 }}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * B': 学员任务管理（v1.2-rc2 P1-i）
 * ────────────────────────────────────────────────────────────────── */
export function TasksPage() {
  const nav = useNavigate();
  const [date, setDate] = useState<string | undefined>();
  const [urgency, setUrgency] = useState<string | undefined>();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<any>({
    queryKey: ['academic-tasks', date, urgency, page],
    queryFn: () => $.get('/academic/tasks', {
      date, urgency, page, pageSize: 30,
    }),
  });

  return (
    <>
      <PageHeader
        breadcrumb={['教务流程', '学员任务管理']}
        title="学员任务管理"
        subtitle="按日期 + 紧急度筛选学员每日任务，点行跳学员详情"
      />
      <div className="work-content">
        <HomeCard title={`任务（${data?.total ?? 0}）`} action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Input type="date" value={date ?? ''} onChange={(e) => setDate(e.target.value || undefined)}
                   style={{ width: 160 }} placeholder="日期筛选" />
            <Select placeholder="紧急度" allowClear style={{ width: 120 }}
                    value={urgency} onChange={setUrgency}
                    options={[
                      { value: 'urgent', label: '紧急' },
                      { value: 'important', label: '重要' },
                      { value: 'normal', label: '一般' },
                    ]} />
          </div>
        }>
          <TableWrap>
            <Table
              rowKey="id"
              loading={isLoading}
              dataSource={data?.items ?? []}
              pagination={{ current: page, pageSize: 30, total: data?.total ?? 0, onChange: setPage, showSizeChanger: false }}
              size="middle"
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: <EmptyHint arrow={false}>暂无数据</EmptyHint> }}
              onRow={(r: any) => {
                const sid = r.record?.id ?? r.academicRecordId;
                return {
                  onClick: () => { if (sid) nav(`/academic/students/${sid}`); else message.warning('该任务无关联学员，无法跳转'); },
                  style: { cursor: sid ? 'pointer' : 'default' },
                };
              }}
              columns={[
                { title: '学员', render: (_v: any, r: any) =>
                  r.customer?.realName ?? r.customer?.nickname ?? `#${r.record?.customerId ?? '?'}`, width: 160 },
                { title: '日期', dataIndex: 'date', width: 110 },
                { title: '今日任务', dataIndex: 'todayTask', ellipsis: true,
                  render: (v: any) => v ?? <span style={{ color: 'var(--text-3, #8F959E)' }}>—</span> },
                { title: '完成', dataIndex: 'todayDone', width: 70, align: 'center' as any,
                  render: (v: number) => v ? <Pill tone="success">✓</Pill> : <Pill tone="default">○</Pill> },
                { title: '学员问题', dataIndex: 'studentQuestion', ellipsis: true,
                  render: (v: any) => v ?? <span style={{ color: 'var(--text-3, #8F959E)' }}>—</span> },
                { title: '紧急', dataIndex: 'questionUrgency', width: 90, align: 'center' as any,
                  render: (v: string) => v ? <Pill tone={URGENCY_TONE[v]}>{URGENCY_LABEL[v]}</Pill> : '—' },
              ]}
            />
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * D：教务总表
 * ────────────────────────────────────────────────────────────────── */
export function OverviewPage() {
  const nav = useNavigate();
  const [stage, setStage] = useState<string | undefined>();
  const [level, setLevel] = useState<string | undefined>();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<any>({
    queryKey: ['academic-overview', { stage, level, page }],
    queryFn: () => $.get('/academic/overview', { stage, level, page, pageSize: 50 }),
  });

  // FAIL1 修(batch-7)：后端 /academic/overview 不 enrich academicUserName(常为 null) → 前端自己拉 users 建 id→姓名 map
  const { data: usersData } = useQuery<any>({
    queryKey: ['users-name-map'],
    // 条8 修：pageSize 500 超 PaginationDto @Max(200) → 400「pageSize格式不正确」红字。
    //   全员约 39 人，200 足够覆盖 id→姓名 map；改回上限内。
    queryFn: () => $.get('/users', { page: 1, pageSize: 200 }),
  });
  const userMap = useMemo(() => {
    const m = new Map<number, string>();
    (usersData?.items ?? []).forEach((u: any) => m.set(Number(u.id), u.realName ?? u.username ?? `#${u.id}`));
    return m;
  }, [usersData]);

  const csvDownload = () => {
    if (!data?.items?.length) return;
    const headers = ['学员', '阶段', '等级', '教务 ID', '上次联系', '服务期截止'];
    const rows = data.items.map((r: any) => [
      r.customer?.realName ?? r.customer?.nickname ?? '',
      STAGE_LABELS[r.currentStage] ?? r.currentStage,
      r.studentLevel,
      r.assignedAcademicId ?? '',
      r.lastContactAt ? dayjs(r.lastContactAt).format('YYYY-MM-DD') : '',
      r.serviceEndAt ?? '',
    ]);
    const csv = [headers, ...rows].map((line: any[]) =>
      line.map((c: any) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `academic-overview-${dayjs().format('YYYYMMDD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        breadcrumb={['教务流程', '教务总表']}
        title="教务总表"
        subtitle="所有教务负责的学员（主管 / 总监 / admin 可看）"
        actions={<Btn kind="default" onClick={csvDownload}>导出 CSV</Btn>}
      />
      <div className="work-content">
        <HomeCard title={`全部学员（${data?.total ?? 0}）`} action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Select placeholder="阶段" allowClear style={{ width: 160 }}
                    value={stage} onChange={setStage}
                    options={STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] }))} />
            <Select placeholder="等级" allowClear style={{ width: 100 }}
                    value={level} onChange={setLevel}
                    options={['A', 'B', 'C'].map((v) => ({ value: v, label: v }))} />
          </div>
        }>
          <TableWrap>
            <Table
              rowKey="id"
              loading={isLoading}
              dataSource={data?.items ?? []}
              pagination={{
                current: page, pageSize: 50, total: data?.total ?? 0,
                onChange: setPage, showSizeChanger: false,
              }}
              size="middle"
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: <EmptyHint arrow={false}>暂无数据</EmptyHint> }}
              onRow={(r: any) => ({ onClick: () => nav(`/academic/students/${r.id}`), style: { cursor: 'pointer' } })}
              columns={[
                { title: '学员', width: 160, render: (_v: any, r: any) => <span style={{ color: 'var(--primary)', fontWeight: 500 }}>{r.customer?.realName ?? r.customer?.nickname ?? '—'}</span> },
                { title: '阶段', dataIndex: 'currentStage', render: (s: string) => STAGE_LABELS[s] ?? s, width: 160 },
                { title: '等级', dataIndex: 'studentLevel', render: (l: string) => <Pill tone={LEVEL_TONE[l]}>{l}</Pill>, width: 80 },
                { title: '教务', dataIndex: 'academicUserName', width: 110,
                  // FAIL1 修(batch-7)：前端 userMap 优先(后端 academicUserName 常 null)，再 fallback enrich 值，最后才 #id
                  render: (v: string, r: any) => userMap.get(Number(r.assignedAcademicId)) ?? v ?? (r.assignedAcademicId ? `#${r.assignedAcademicId}` : '—') },
                // v1.2 教务增强 C-3 视角②：指导进度列（部门领导一眼看全员状态）
                { title: '指导进度', width: 180,
                  render: (_v: any, r: any) => (
                    <OverviewInsightCell recordId={Number(r.id)} />
                  ) },
                { title: '企微', dataIndex: 'weworkBindStatus', width: 90,
                  render: (v: string) => (
                    <Pill tone={v === 'confirmed' ? 'success' : v === 'auto_matched' ? 'warning' : 'default'}>
                      {v === 'confirmed' ? '✓ 已绑' : v === 'auto_matched' ? '待确认' : '未绑'}
                    </Pill>
                  ) },
                { title: '上次联系', dataIndex: 'lastContactAt', render: (d: string) => fmtRelative(d), width: 140 },
                { title: '服务期截止', dataIndex: 'serviceEndAt', width: 120 },
              ]}
            />
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * E：教务日报
 * ────────────────────────────────────────────────────────────────── */
export function ReportsPage() {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const isApprover = me && ['academic_director', 'admin'].includes(me.role);
  const [tab, setTab] = useState<string>(isApprover ? 'pending' : 'mine');

  const { data: reports } = useQuery<any[]>({
    queryKey: ['academic-reports', tab],
    queryFn: () => $.get('/academic/reports',
      tab === 'pending' ? { status: 'pending' } : {}),
  });

  const submit = useMutation({
    mutationFn: () => $.post('/academic/reports/submit', {}),
    onSuccess: () => {
      message.success('已提交今日日报');
      qc.invalidateQueries({ queryKey: ['academic-reports'] });
    },
    onError: (e: any) => message.error(e?.message ?? '提交失败'),
  });

  const approve = useMutation({
    mutationFn: (id: number) => $.post(`/academic/reports/${id}/approve`, {}),
    onSuccess: () => {
      message.success('已通过');
      qc.invalidateQueries({ queryKey: ['academic-reports'] });
    },
  });

  const reject = useMutation({
    mutationFn: (p: { id: number; reason: string }) =>
      $.post(`/academic/reports/${p.id}/reject`, { reason: p.reason }),
    onSuccess: () => {
      message.success('已退回');
      qc.invalidateQueries({ queryKey: ['academic-reports'] });
    },
  });

  const onReject = (id: number) => {
    let reason = '';
    Modal.confirm({
      title: '退回日报',
      content: (
        <Input.TextArea
          placeholder="请填写退回原因（必填）"
          rows={3}
          onChange={(e) => { reason = e.target.value; }}
        />
      ),
      onOk: () => {
        if (!reason.trim()) {
          message.error('请填写退回原因');
          return Promise.reject();
        }
        return new Promise<void>((resolve, ref) => {
          reject.mutate({ id, reason }, {
            onSuccess: () => resolve(),
            onError: (e: any) => { message.error(e?.message ?? '退回失败'); ref(); },
          });
        });
      },
    });
  };

  return (
    <>
      <PageHeader
        breadcrumb={['教务流程', '教务日报']}
        title="教务日报"
        subtitle={isApprover ? '主管审批未审 / 已审' : '提交今日日报 + 查看历史'}
        actions={
          !isApprover && (
            <Btn kind="primary" disabled={submit.isPending} onClick={() => submit.mutate()}>
              {submit.isPending ? '提交中…' : '提交今日日报'}
            </Btn>
          )
        }
      />
      <div className="work-content">
        <Tabs activeKey={tab} onChange={setTab} style={{ marginBottom: 12 }}
              items={[
                ...(isApprover ? [{ key: 'pending', label: '待审批' }] : []),
                { key: 'mine', label: isApprover ? '所有日报' : '我的日报' },
              ]}
        />
        <HomeCard title={`日报（${reports?.length ?? 0}）`}>
          <TableWrap>
            <Table
              rowKey="id"
              dataSource={reports ?? []}
              pagination={{ pageSize: 30 }}
              size="middle"
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: <EmptyHint arrow={false}>暂无数据</EmptyHint> }}
              columns={[
                { title: '日期', dataIndex: 'date', width: 120 },
                { title: '教务', dataIndex: 'academicUserName', width: 110,
                  render: (v: string, r: any) => v ?? `#${r.academicUserId}` },
                { title: '学员', render: (_v: any, r: any) => r.reportData?.total_students ?? 0, width: 80 },
                { title: '今日任务', render: (_v: any, r: any) => `${r.reportData?.tasks_today_done ?? 0} / ${r.reportData?.tasks_today_count ?? 0}`, width: 110 },
                { title: '紧急问题', render: (_v: any, r: any) => r.reportData?.urgent_question_count ?? 0, width: 100 },
                { title: '状态', dataIndex: 'approvalStatus', width: 100,
                  render: (s: string) => (
                    <Pill tone={s === 'approved' ? 'success' : s === 'rejected' ? 'danger' : 'warning'}>
                      {s === 'approved' ? '✓ 已通过' : s === 'rejected' ? '↩ 已退回' : '待审'}
                    </Pill>
                  ),
                },
                { title: '操作', width: 180,
                  render: (_v: any, r: any) => (
                    isApprover && r.approvalStatus === 'pending' ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Btn kind="primary" onClick={() => approve.mutate(r.id)}>通过</Btn>
                        <Btn kind="default" onClick={() => onReject(r.id)}>退回</Btn>
                      </div>
                    ) : r.approvalStatus === 'rejected' ? (
                      <span title={r.rejectionReason} style={{ fontSize: 12, color: 'var(--danger, #FF4D4F)', display: 'block', maxWidth: 168, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.rejectionReason}
                      </span>
                    ) : '—'
                  ),
                },
              ]}
            />
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * F：仪表盘
 * ────────────────────────────────────────────────────────────────── */
export function DashboardPage() {
  const nav = useNavigate();
  const { data, isLoading } = useQuery<any>({
    queryKey: ['academic-dashboard'],
    queryFn: () => $.get('/academic/dashboard'),
  });

  if (isLoading || !data) return null;

  const totalLevel = data.levelDistribution.reduce((a: number, b: any) => a + b.count, 0) || 1;
  const totalStage = data.stageDistribution.reduce((a: number, b: any) => a + b.count, 0) || 1;

  return (
    <>
      <PageHeader
        breadcrumb={['教务流程', '教务仪表盘']}
        title="教务仪表盘"
        subtitle="本部门 A/B/C 分布 + 9 阶段 + 完成率排行 + 服务期到期 + 紧急问题"
      />
      <div className="work-content">
        {/* 顶部 stat */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
          {data.levelDistribution.map((d: any) => (
            <StatCard key={d.level}
              label={`等级 ${d.level}`}
              value={d.count}
              unit="人"
              valueColor={d.level === 'A' ? 'success' : d.level === 'B' ? 'primary' : 'warning'}
            />
          ))}
          <StatCard label="紧急问题" value={data.urgentQuestions?.length ?? 0} unit="条"
                    valueColor={(data.urgentQuestions?.length ?? 0) > 0 ? 'danger' : 'primary'} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
          {/* 9 阶段堆叠 */}
          <HomeCard title="9 阶段人数分布">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 4 }}>
              {/* batch-4 P2.4：用全 9 阶段渲染(后端只返 count>0 的阶段)，count=0 也成行可见 */}
              {STAGES.map((stage) => {
                const s = data.stageDistribution.find((x: any) => x.stage === stage);
                const count = s?.count ?? 0;
                const pct = (count / totalStage) * 100;
                return (
                  <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ width: 130, color: 'var(--text-2, #4E5969)' }}>{STAGE_LABELS[stage]}</span>
                    <div style={{ flex: 1, height: 18, background: 'var(--bg-2, #F4F5F7)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${count > 0 ? Math.max(2, pct) : 0}%`, height: '100%',
                        background: 'var(--primary, #3370FF)',
                      }} />
                    </div>
                    <span style={{ width: 40, textAlign: 'right', color: count > 0 ? 'var(--text-1)' : 'var(--text-3)' }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </HomeCard>

          {/* 完成率排行 */}
          <HomeCard title="教务完成率排行（今日）">
            {(data.completionRanking ?? []).length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--text-3, #8F959E)', textAlign: 'center' }}>暂无数据</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.completionRanking.map((r: any, i: number) => (
                  <div key={r.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ width: 24, color: 'var(--text-3, #8F959E)' }}>#{i + 1}</span>
                    <span style={{ flex: 1 }}>{r.realName ?? `#${r.userId}`}</span>
                    <span style={{ color: 'var(--text-2, #4E5969)' }}>{r.doneTasks} / {r.totalTasks}</span>
                    <Pill tone={r.rate >= 0.8 ? 'success' : r.rate >= 0.5 ? 'primary' : 'warning'}>
                      {(r.rate * 100).toFixed(0)}%
                    </Pill>
                  </div>
                ))}
              </div>
            )}
          </HomeCard>

          {/* 服务期到期 */}
          <HomeCard title="服务期 30 天内到期">
            {(data.serviceExpiringSoon ?? []).length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--text-3, #8F959E)', textAlign: 'center' }}>暂无到期学员</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.serviceExpiringSoon.map((s: any) => (
                  <div key={s.recordId}
                       onClick={() => nav(`/academic/students/${s.recordId}`)}
                       style={{
                         display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                         padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
                       }}>
                    <span style={{ flex: 1 }}>{s.nickname ?? '—'}</span>
                    <Pill tone={LEVEL_TONE[s.studentLevel]}>{s.studentLevel}</Pill>
                    <span style={{ color: 'var(--warning-text)' }}>{s.serviceEndAt}</span>
                  </div>
                ))}
              </div>
            )}
          </HomeCard>

          {/* 紧急问题 top 5 */}
          <HomeCard title="紧急学员问题 Top 5">
            {(data.urgentQuestions ?? []).length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--text-3, #8F959E)', textAlign: 'center' }}>无紧急问题</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.urgentQuestions.map((q: any) => (
                  <div key={q.taskId}
                       onClick={() => nav(`/academic/students/${q.recordId}`)}
                       style={{
                         padding: 10, background: 'var(--bg-2, #F4F5F7)', borderRadius: 8,
                         fontSize: 13, cursor: 'pointer',
                       }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                      <Pill tone={URGENCY_TONE[q.urgency]}>{URGENCY_LABEL[q.urgency]}</Pill>
                      <span style={{ color: 'var(--text-2, #4E5969)' }}>{q.customerNickname} · {q.date}</span>
                    </div>
                    <div style={{ color: 'var(--text-1, #1F2329)', lineHeight: 1.5 }}>
                      {(q.question ?? '').slice(0, 120)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </HomeCard>
        </div>

        {/* Wave-Viz 5：教务交付进度看板（各班型完成度分布 + 逾期任务数） */}
        <AcademicDeliveryBoard />
      </div>
    </>
  );
}
